import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AUTH_MODE, canRefresh, completeLogin, ensureSession, login, logout, session, startTwoStepEnrollment, twoStepStatus, verifyTwoStep } from './lib/auth.ts';
import { api, useApi, type ApiFailure } from './lib/api.ts';
import { ErrorState, Icon, Loading, OrgCtx, ToastProvider, can, type OrgInfo } from './lib/ui.tsx';
import { Overview } from './pages/Overview.tsx';
import { Screens } from './pages/Screens.tsx';
import { Locations } from './pages/Locations.tsx';
import { Library } from './pages/Library.tsx';
import { Playlists } from './pages/Playlists.tsx';
import { Schedule } from './pages/Schedule.tsx';
import { Publish, Publications } from './pages/Publish.tsx';
import { Alerts } from './pages/Alerts.tsx';
import { Campaigns } from './pages/Campaigns.tsx';
import { Music } from './pages/Music.tsx';
import { Reports } from './pages/Reports.tsx';
import { Integrations } from './pages/Integrations.tsx';
import { Settings } from './pages/Settings.tsx';
import { Studio } from './pages/Studio.tsx';
import { Streams } from './pages/Streams.tsx';

const NAV: { group?: string; to: string; label: string; icon: string; perm?: string }[] = [
  { to: '/', label: 'Overview', icon: 'overview' },
  { to: '/screens', label: 'Screens', icon: 'screens', perm: 'screens.read' },
  { to: '/locations', label: 'Locations', icon: 'locations', perm: 'locations.read' },
  { group: 'Content', to: '/library', label: 'Library', icon: 'library', perm: 'assets.read' },
  { to: '/streams', label: 'Streams', icon: 'play', perm: 'assets.read' },
  { to: '/studio', label: 'Studio', icon: 'studio', perm: 'assets.write' },
  { to: '/playlists', label: 'Playlists', icon: 'playlists', perm: 'assets.read' },
  { to: '/schedule', label: 'Schedule', icon: 'schedule', perm: 'assets.read' },
  { to: '/publish', label: 'Publish', icon: 'publish', perm: 'publish.create' },
  { group: 'Growth', to: '/campaigns', label: 'Campaigns', icon: 'campaigns', perm: 'campaigns.read' },
  { to: '/music', label: 'Music', icon: 'music', perm: 'assets.read' },
  { group: 'Insight', to: '/reports', label: 'Reports', icon: 'reports', perm: 'reports.read' },
  { to: '/alerts', label: 'Alerts', icon: 'alerts', perm: 'screens.read' },
  {
    group: 'Admin',
    to: '/integrations',
    label: 'Integrations',
    icon: 'integrations',
    perm: 'integrations.manage',
  },
  { to: '/settings', label: 'Settings', icon: 'settings' },
];

function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(
    () =>
      localStorage.getItem('lumacast.theme') ??
      (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
  );
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('lumacast.theme', theme);
  }, [theme]);
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}

function Callback() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    completeLogin().then(
      (to) => nav(to, { replace: true }),
      (e: Error) => setError(e.message),
    );
  }, [nav]);
  return (
    <div className="empty" style={{ marginTop: '20vh' }}>
      {error ? (
        <>
          <h3>Sign-in failed</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => void login('/')}>
            Try again
          </button>
        </>
      ) : (
        <h3>Signing you in…</h3>
      )}
    </div>
  );
}

/** Google mode (ADR-0026): the person chooses to sign in; no automatic redirect to Google. */
function SignInPage() {
  const [busy, setBusy] = useState(false);
  return (
    <main className="empty" style={{ marginTop: '18vh' }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>LumaCast</h1>
      <p style={{ maxWidth: 420, marginInline: 'auto' }}>Sign in with the Google account your organization invited.</p>
      <button className="btn btn-primary" disabled={busy} onClick={() => (setBusy(true), void login(location.pathname === '/' ? '/' : location.pathname + location.search))}>
        Sign in with Google
      </button>
    </main>
  );
}

/**
 * Two-step verification for owners and admins who sign in with Google: set up an authenticator
 * app once (scan the QR code), then enter its 6-digit code once per sign-in.
 */
function TwoStep({ orgName, onDone }: { orgName: string; onDone: () => void }) {
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [setup, setSetup] = useState<{ secret: string; qrSvg: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    twoStepStatus().then(
      (s) => (s.satisfied ? onDone() : setEnrolled(s.enrolled)),
      (e: Error) => setError(e.message),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (enrolled === false && !setup) startTwoStepEnrollment().then((r) => setSetup(r), (e: Error) => setError(e.message));
  }, [enrolled, setup]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyTwoStep(code.replace(/\s/g, ''));
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setCode('');
    } finally {
      setBusy(false);
    }
  }
  const qr = setup ? `data:image/svg+xml;base64,${btoa(setup.qrSvg)}` : null;
  return (
    <main className="empty" style={{ marginTop: '12vh' }}>
      <h3>{enrolled === false ? 'Set up two-step verification' : 'Two-step verification'}</h3>
      <p style={{ maxWidth: 520, marginInline: 'auto' }}>
        {enrolled === false
          ? `Your role in ${orgName} needs a second step after Google. Open an authenticator app (Google Authenticator, Microsoft Authenticator or similar), scan this code, then enter the 6-digit number it shows.`
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      {qr && (
        <div style={{ display: 'grid', justifyItems: 'center', gap: 8, margin: '16px 0' }}>
          <img src={qr} alt="QR code to add LumaCast to your authenticator app" width={200} height={200} style={{ background: '#fff', padding: 8, borderRadius: 8 }} />
          <details>
            <summary>Can’t scan? Enter this key instead</summary>
            <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{setup!.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
          </details>
        </div>
      )}
      {enrolled !== null && (enrolled || setup) && (
        <form onSubmit={(e) => void submit(e)} style={{ display: 'grid', justifyItems: 'center', gap: 12 }}>
          <div className="field" style={{ width: 220 }}>
            <label htmlFor="two-step-code">6-digit code</label>
            <input id="two-step-code" className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} autoFocus style={{ textAlign: 'center', letterSpacing: 4, fontSize: 20 }} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={busy || code.replace(/\s/g, '').length !== 6}>
            {enrolled === false ? 'Turn on two-step verification' : 'Verify'}
          </button>
        </form>
      )}
      {error && (
        <p role="alert" style={{ color: 'var(--danger, #C01C38)', marginTop: 12 }}>
          {error}
        </p>
      )}
      <p style={{ marginTop: 16 }}>
        <button className="btn btn-ghost" onClick={() => void logout()}>
          Sign out
        </button>
      </p>
    </main>
  );
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const items = useMemo(() => {
    const base = [
      ...NAV.map((n) => ({ label: `Go to ${n.label}`, hint: 'Navigate', run: () => nav(n.to) })),
      { label: 'Pair a screen', hint: 'Screens', run: () => nav('/screens?pair=1') },
      { label: 'Upload media', hint: 'Library', run: () => nav('/library?upload=1') },
      { label: 'Publish to screens', hint: 'Publish', run: () => nav('/publish') },
      { label: 'New playlist', hint: 'Playlists', run: () => nav('/playlists?new=1') },
    ];
    return base.filter((x) => x.label.toLowerCase().includes(q.toLowerCase()));
  }, [q, nav]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <input
          className="input"
          autoFocus
          placeholder="Type a command or search…"
          value={q}
          aria-label="Command"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
          onChange={(e) => (setQ(e.target.value), setI(0))}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setI((x) => Math.min(x + 1, items.length - 1));
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setI((x) => Math.max(x - 1, 0));
            }
            if (e.key === 'Enter' && items[i]) {
              items[i]!.run();
              onClose();
            }
          }}
        />
        <ul id="palette-list" role="listbox">
          {items.map((x, n) => (
            <li
              key={x.label}
              role="option"
              aria-selected={n === i}
              onMouseEnter={() => setI(n)}
              onClick={() => (x.run(), onClose())}
            >
              <span>{x.label}</span>
              <span className="subtle">{x.hint}</span>
            </li>
          ))}
          {!items.length && <li aria-disabled="true">No matching commands</li>}
        </ul>
      </div>
    </div>
  );
}

function Shell({
  orgs,
  org,
  setOrgId,
  children,
}: {
  orgs: { id: string; name: string }[];
  org: OrgInfo;
  setOrgId: (id: string) => void;
  children: ReactNode;
}) {
  const [theme, toggle] = useTheme();
  const [palette, setPalette] = useState(false);
  const [menu, setMenu] = useState(false);
  const alerts = useApi<{ alerts: unknown[] }>(
    can(org, 'screens.read') ? `/v1/orgs/${org.id}/alerts` : null,
    [org.id],
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((p) => !p);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const s = session();
  let lastGroup: string | undefined;
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <nav className={`sidebar${menu ? ' open' : ''}`} aria-label="Primary">
        <div className="brand">
          <span className="brand-mark" aria-hidden>
            L
          </span>
          <span className="label-text">LumaCast</span>
        </div>
        {NAV.filter((n) => !n.perm || can(org, n.perm)).map((n) => {
          const header = n.group && n.group !== lastGroup ? <div className="nav-group">{n.group}</div> : null;
          if (n.group) lastGroup = n.group;
          return (
            <div key={n.to}>
              {header}
              <NavLink
                to={n.to}
                end={n.to === '/'}
                className="nav-link"
                onClick={() => setMenu(false)}
                title={n.label}
              >
                <Icon name={n.icon} />
                <span className="label-text">{n.label}</span>
                {n.to === '/alerts' && !!alerts.data?.alerts.length && (
                  <span className="nav-badge" aria-label={`${alerts.data.alerts.length} open alerts`}>
                    {alerts.data.alerts.length}
                  </span>
                )}
              </NavLink>
            </div>
          );
        })}
      </nav>
      <div className="main">
        <header className="toolbar">
          <button
            className="btn btn-ghost icon-btn menu-toggle"
            aria-label="Open navigation"
            aria-expanded={menu}
            onClick={() => setMenu((m) => !m)}
          >
            <Icon name="menu" />
          </button>
          {orgs.length > 1 ? (
            <select
              className="input"
              style={{ width: 'auto', minHeight: 34 }}
              value={org.id}
              onChange={(e) => setOrgId(e.target.value)}
              aria-label="Organization"
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          ) : (
            <strong>{org.name}</strong>
          )}
          <span className="badge plain hide-mobile">{org.role.replace('_', ' ')}</span>
          <div className="spacer" />
          <button
            className="btn btn-ghost hide-mobile"
            onClick={() => setPalette(true)}
            aria-keyshortcuts="Control+K Meta+K"
          >
            <Icon name="search" /> Search <kbd className="subtle">⌘K</kbd>
          </button>
          <button
            className="btn btn-ghost icon-btn"
            onClick={toggle}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
          </button>
          <span className="subtle hide-mobile">{s?.email}</span>
          <button className="btn btn-ghost icon-btn" onClick={() => void logout()} aria-label="Sign out">
            <Icon name="logout" />
          </button>
        </header>
        <main id="main" className="content" tabIndex={-1}>
          {children}
        </main>
      </div>
      {palette && <CommandPalette onClose={() => setPalette(false)} />}
    </div>
  );
}

function Authed() {
  const me = useApi<{ organizations: { id: string; name: string; role: string }[] }>('/v1/me');
  const [orgId, setOrgId] = useState<string | null>(() => localStorage.getItem('lumacast.org'));
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [orgError, setOrgError] = useState<ApiFailure | null>(null);
  const [attempt, setAttempt] = useState(0);
  const orgs = me.data?.organizations ?? [];
  const active = orgs.find((o) => o.id === orgId) ?? orgs[0];
  useEffect(() => {
    if (!active) return;
    localStorage.setItem('lumacast.org', active.id);
    void api<{
      organization: { id: string; name: string };
      role: string;
      permissions: string[];
      locationScope: string[] | null;
    }>('GET', `/v1/orgs/${active.id}`).then(
      (o) => {
        setOrgError(null);
        setOrg({
          id: o.organization.id,
          name: o.organization.name,
          role: o.role,
          permissions: o.permissions,
          locationScope: o.locationScope,
        });
      },
      (e: ApiFailure) => setOrgError(e),
    );
  }, [active?.id, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (me.error)
    return (
      <div className="content">
        <ErrorState error={me.error} retry={me.reload} />
      </div>
    );
  if (orgError?.code === 'mfa_required' && AUTH_MODE === 'google')
    return <TwoStep orgName={active?.name ?? 'this organization'} onDone={() => (setOrgError(null), setAttempt((n) => n + 1))} />;
  if (orgError?.code === 'mfa_required')
    return (
      <div className="empty" style={{ marginTop: '20vh' }}>
        <h3>Two-step verification required</h3>
        <p style={{ maxWidth: 520, marginInline: 'auto' }}>
          Your role in {active?.name ?? 'this organization'} requires signing in with a second factor, such as an authenticator app or security key.
          If you have not set one up yet, set it up with your organization’s sign-in provider first.
        </p>
        <button className="btn btn-primary" onClick={() => void login(location.pathname + location.search, { mfa: true })}>
          Sign in with two-step verification
        </button>
      </div>
    );
  if (orgError)
    return (
      <div className="content">
        <ErrorState error={orgError} />
      </div>
    );
  if (me.loading || (active && !org))
    return (
      <div className="content">
        <Loading />
      </div>
    );
  if (!active)
    return (
      <div className="empty" style={{ marginTop: '20vh' }}>
        <h3>No organization yet</h3>
        <p>Ask an administrator to invite you to a LumaCast organization.</p>
      </div>
    );
  return (
    <OrgCtx.Provider value={org}>
      <Shell orgs={orgs} org={org!} setOrgId={(id) => (setOrg(null), setOrgId(id))}>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/screens" element={<Screens />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/library" element={<Library />} />
          <Route path="/streams" element={<Streams />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/playlists" element={<Playlists />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/publish" element={<Publish />} />
          <Route path="/publications" element={<Publications />} />
          <Route path="/publications/:publicationId" element={<Publications />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/music" element={<Music />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
    </OrgCtx.Provider>
  );
}

function Gate() {
  const [s, setS] = useState(session);
  const [checking, setChecking] = useState(!s && AUTH_MODE === 'google' && canRefresh());
  useEffect(() => {
    if (s) return;
    if (AUTH_MODE === 'google') {
      // Renew a sign-in this tab already has; otherwise show the sign-in page.
      if (checking) void ensureSession().then((n) => (setS(n), setChecking(false)));
      return;
    }
    void login();
  }, [s, checking]);
  if (!s && AUTH_MODE === 'google') return checking ? <Loading /> : <SignInPage />;
  return s ? (
    <Authed />
  ) : (
    <div className="empty" style={{ marginTop: '20vh' }}>
      <h3>Redirecting to sign-in…</h3>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          <Route path="/auth/callback" element={<Callback />} />
          <Route path="/*" element={<Gate />} />
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}
